import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import CustomButton from "../../componentsII/Buttons";
import { useForm, Controller } from "react-hook-form";
import {
  FormControl,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { useSelector } from "react-redux";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import CustomCard from "../../componentsII/Card"
import api from "../../axios/api";
import { showNotification } from "../../redux/slices/notificationSlice";
import ConfirmationModal from "../../componentsII/ConfirmationModal";

const RefrigerantConsumptionDetailModal = ({
  detailModalData: initialData,
  refetch,
  handleCloseMainDetailModal,
}) => {
  const dispatch = useDispatch();
  const [detailModalData, setDetailModalData] = useState({ ...initialData });
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditable, setIsEditable] = useState(false);
  const [fuelUnit, setFuelUnit] = useState([]);
  const [refrigerantTypeOptions, setRefrigerantTypeOptions] = useState([]);
  const year = useSelector((state) => state.user.selectedYear);


  const schema = Yup.object().shape({
    refrigerant_qty: Yup.number().required("Refrigerant quantity is required"),
    refrigerant_type_id: Yup.number().required("Refrigrant type is required").test('not-zero', 'Refrigrant type is required', value => value !== 0),
    qty_unit_id: Yup.number().required("Unit is required").test('not-zero', 'Unit is required', value => value !== 0),
});

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      id: detailModalData.refrigerant?.refrigerant_id || 0,
      refrigerant_qty: detailModalData.refrigerant?.refrigerant_qty || null,
      qty_unit_id: detailModalData.refrigerant?.qty_unit_id || 0,
      refrigerant_type_id: detailModalData.refrigerant?.refrigerant_type_id || 0,
      equipment_id: detailModalData.id || null,
      year:year.start_year,
    },
  });

  const getFuelUnit = async (typeId) => {
    try {
      // const res = await api.post(`/common/measurementType`, { type_code: ["LIQUID", "MASS", "VOLUME"] });
      const res = await api.get(`/common/measurementType?type_code=MASS`);
      setFuelUnit(res.data.data);
    } catch (error) {
      console.error("Error fetching models:", error);
    }
  };

  const fetchRefrigerantTypeOptions = async () => {
    try {
      const res = await api.get(`/common/refrigerantType`);
      setRefrigerantTypeOptions(res.data.data);
    } catch (error) {
      console.error("Error fetching models:", error);
    }
  };

  const handleEditSubmit = async (data) => {
    const body = { ...data,year:year.start_year };
    try {
      const response = await api.post(
        "/equipment/addUpdateRefrigerantConsumption",
        body
      );
      if (response.data.status === "200") {
        dispatch(
          showNotification({ message: response.data.message, type: "success" })
        );
        refetch();
        handleCloseMainDetailModal();
      } else {
        dispatch(
          showNotification({ message: response.data.message, type: "error" })
        );
        setIsEditable(false);
        console.log(response.data.message);
      }
      console.log("Form submitted successfully:", response);
    } catch (error) {
      dispatch(
        showNotification({
          message: error.message,
          type: "error",
        })
      );
      console.error("Error submitting form:", error);
    }
  };

  useEffect(() => {
    getFuelUnit();
    fetchRefrigerantTypeOptions();
  }, []);


  const handleDelete = async () => {
    try {
      const response = await api.delete(
        `/equipment/deleteRefrigerantConsumption/${detailModalData?.refrigerant.refrigerant_id}`
      );
      if (response.data) {
        dispatch(
          showNotification({
            message: response.data.message,
            type: "success",
          })
        );
      } else {
        dispatch(
          showNotification({
            message: response.data.message,
            type: "error",
          })
        );
        console.log(response.data.message);
      }
      refetch();
      handleCloseMainDetailModal();
    } catch (error) {
      console.error("Error fetching data:", error);
    }
    setIsDeleteModalOpen(false);
  };

  return (
    <div className="h-full">
      <div>
        <div className="flex border-b border-dashed border-gray-500 ">
          <div className="px-8 w-full text-center">
            <h1 className="text-2xl font-bold text-green-600">
              {initialData?.refrigerant?.refrigerant_id ? "Edit" : "Add"} {year?.start_year} Refrigerant data
            </h1>
            <p className="text-gray-500 mb-5 mt-1 text-md font-medium">
              to add your refrigerant consumption data,you'll need the type of refrigerant used and the amount (e.g.145 Kilograms) consumed during {year?.start_year}
            </p>
          </div>
        </div>

        <div className="px-6">
          <div className="flex justify-center w-full items-center py-6">
            <div className="w-[60%] rounded-[20px] border-[10px] shadow-md">
              <CustomCard
                image={detailModalData.img}
                title={detailModalData.equipment_name_code}
                subtitleI={`${detailModalData?.equipment_type_name || ""}`}
                subtitleII={detailModalData?.comp_location_name || ""}
                subtitleIII={
                  detailModalData?.veh_subtype_details?.sub_type_name || ""
                }
              />
            </div>
          </div>
          <form onSubmit={handleSubmit(handleEditSubmit)}>
            <div className="flex justify-center items-center w-50%">
              <CompSelect
                title="what kind of refrigerant does cold storage use?"
                value={initialData.refrigerant_type_id}
                name="refrigerant_type_id"
                isError={!!errors.qty_unit_id}
                errorText={errors.qty_unit_id?.message}
                control={control}
                isEditable={isEditable}
                options={refrigerantTypeOptions}
              />
            </div>
            <div className="bg-gray-200 p-9 rounded-md m-5">
              Looking to Add refrigerant data for R22 (Freon),R290(Propane),R600(isobutane),or other refrigerants
              not in the dropdown list above? According to the Greenhouse Gas Protocol,only emissions from the seven
              greenhouse gases as classified by the Kyoto Protocol should be included in your inventory.So if you do not see the
              refrigerant that your equipment uses on the list,it is likely that it is made of non-kyoto gases and should be excluded.
            </div>
            <div className="grid grid-cols-2 gap-4">
              <CompInput
                title={`How much refrigerant did this ${detailModalData.equipment_name_code} used in ${year?.start_year} ?`}
                value={initialData.refrigerant_qty}
                name="refrigerant_qty"
                isError={!!errors.refrigerant_qty}
                errorText={errors.refrigerant_qty?.message}
                control={control}
                isEditable={isEditable}
                type="number"
              />
              <CompSelect
                title="unit"
                value={initialData.qty_unit_id}
                name="qty_unit_id"
                isError={!!errors.qty_unit_id}
                errorText={errors.qty_unit_id?.message}
                control={control}
                isEditable={isEditable}
                options={fuelUnit}
              />
            </div>

            <div className="flex justify-between items-center w-full py-2">
              {initialData?.refrigerant?.refrigerant_id ?
                (<div>
                  <CustomButton
                    className="bg-red-500 hover:bg-red-700 text-white "
                    click={() => setIsDeleteModalOpen(true)}
                    text="Delete"
                  />
                </div>) : (<></>)
              }

              <div className="w-full flex justify-end gap-5 items-center py-5">
                <CustomButton
                  className="bg-gray-500 hover:bg-gray-700 text-white "
                  click={() => handleCloseMainDetailModal(false)}
                  text="Cancel"
                />
                <CustomButton
                  className="bg-green-600 hover:bg-green-700 text-white "
                  type="submit"
                  text="Submit"
                />
              </div>
            </div>
          </form>
        </div>
      </div>
      <ConfirmationModal
        isModalOpen={isDeleteModalOpen}
        cancelText="Cancel"
        confirmText="Delete"
        onConfirmClick={handleDelete}
        onCancelClick={() => setIsDeleteModalOpen(false)}
        text={
          <>
            <p>Are you sure you want to delete this item?</p>
            <p>This action cannot be undone.</p>
          </>
        }
        header={<div className="text-red-500">Delete Refrigerant Consumption</div>}
      />
    </div>
  );
};

export default RefrigerantConsumptionDetailModal;

const CompInput = ({
  title,
  value,
  isBorderBottom = true,
  isEditable = false,
  control,
  isError,
  errorText,
  name,
  type="text",
  required=true
}) => {
  return (
    <div className={`h-16 w-full items-center`}>
      <div className="font-medium text-gray-900 flex gap-1 ">
        {title}
        <p className={`${required ? "text-red-500" : "invisible"}`}>*</p>
      </div>   
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <TextField
            type={type}
            {...field}
            fullWidth
            error={isError}
            helperText={errorText}
            placeholder="Enter Value"
            margin="none"
            onKeyDown={(e) => {
              if (
                !/[0-9]/.test(e.key) && 
                !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key) 
              ) {
                e.preventDefault();
              }
            }}
            InputProps={{
              disableUnderline: true,
              sx: {
                padding: 0,
                "& .MuiInputBase-input": {
                  padding: "10px",
                },
              },
            }}
            sx={{
              padding: 0,
              margin: 0,
            }}
            onBlur={(e) => {
              if(type == "number") {
                const formattedValue = parseFloat(e.target.value).toFixed(2);
                field.onChange(formattedValue);
              }
            }}
          />
        )}
      />
    </div>
  );
};



const CompSelect = ({
  title,
  value,
  isBorderBottom = true,
  isEditable = false,
  control,
  isError,
  errorText,
  name,
  options,
  required=true,
  handleChange = () => { },
}) => {
  return (
    <div className={`h-16 w-full items-center`}>
      <div className="font-medium text-gray-900 flex gap-1 ">
        {title}
        <p className={`${required ? "text-red-500" : "invisible"}`}>*</p>
      </div> 
      <div className="">
        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <FormControl fullWidth error={isError}>
              <Select
                {...field}
                sx={{
                  padding: 0, // Removes padding in the Select component itself
                  "& .MuiInputBase-input": {
                    padding: "10px !important", // Removes padding from the input area
                  },
                }}
                onChange={(e) => {
                  field.onChange(e);
                  handleChange(e.target.value);
                }}
                renderValue={(selected) => {
                  const selectedVal = options.find((item) => item?.value == selected)
                  if (selectedVal == undefined) {
                    return <em>Select option</em>;
                  }
                  return selectedVal?.label;
                }}
              >
                <MenuItem key={1100000} value="" disabled>
                  Select option
                </MenuItem>
                {options.map((make) => (
                  <MenuItem key={make.value} value={make.value}>
                    {make.label}
                  </MenuItem>
                ))}
              </Select>
              {errorText && <p className="text-xs text-red-500">{errorText}</p>}
            </FormControl>
          )}
        />
      </div>
    </div>
  );
};
